import { AverageWaitTime } from "@/components/units/BranchReservationForm/types";
import { createCashbackOverrideTargetsResolver } from "@/components/units/EntityHistory/utils/cashbackOverrideResolvers";
import { formatOpeningHoursForDisplay } from "@/utils/formatWorkingHours";

import type { FieldResolver } from "./getChanges";
import { MEDIA_URL_FIELD_RESOLVER } from "./mediaUrlResolver";

const formatZeroBasedOrderValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return String(numericValue + 1);
};

const createZeroBasedOrderResolver = (): FieldResolver => ({
  formatValue: formatZeroBasedOrderValue,
});

const getNestedValue = (
  snapshot: Record<string, unknown>,
  nestedKey: string,
  topLevelKey: string
): unknown => {
  const nested = snapshot[nestedKey];

  if (nested && typeof nested === "object" && topLevelKey in nested) {
    return (nested as Record<string, unknown>)[topLevelKey];
  }

  return snapshot[topLevelKey];
};

const createCredentialsResolver = (key: string): FieldResolver => ({
  getValue: snapshot => getNestedValue(snapshot, "credentials", key),
});

export const HISTORY_FIELD_RESOLVERS = {
  brandDetails: {
    logoUrl: MEDIA_URL_FIELD_RESOLVER,
  },

  branchMedia: {
    mediaItems: MEDIA_URL_FIELD_RESOLVER,
  },

  branchPOS: {
    baseUrl: createCredentialsResolver("baseUrl"),
    client_id: createCredentialsResolver("client_id"),
    branch_id: createCredentialsResolver("branch_id"),
    tenderId: createCredentialsResolver("tenderId"),
    discountId: createCredentialsResolver("discountId"),
  },

  branchReceiptPicture: {
    branchReceiptMedia: MEDIA_URL_FIELD_RESOLVER,
    branchReceiptBillNumberMedia: MEDIA_URL_FIELD_RESOLVER,
    branchReceiptOrderTotalMedia: MEDIA_URL_FIELD_RESOLVER,
  },

  branchHours: {
    openingHours: {
      getValue: snapshot => {
        const openingHours = snapshot.openingHours;

        if (
          openingHours &&
          typeof openingHours === "object" &&
          "schedules" in openingHours
        ) {
          return (openingHours as { schedules: unknown }).schedules;
        }

        return openingHours;
      },
      formatValue: formatOpeningHoursForDisplay,
    },
  },

  branchVibes: {
    priceLevel: {
      formatValue: value => {
        const level = typeof value === "number" ? value : Number(value);

        if (!level || Number.isNaN(level)) {
          return null;
        }

        return "$".repeat(level);
      },
    },
    averageWaitTime: {
      formatValue: value => {
        if (typeof value !== "string") {
          return value === null || value === undefined ? null : String(value);
        }

        return AverageWaitTime[value as keyof typeof AverageWaitTime] ?? value;
      },
    },
  },

  brandMenuDish: {
    displayOrder: createZeroBasedOrderResolver(),
    order: createZeroBasedOrderResolver(),
    mediaItems: MEDIA_URL_FIELD_RESOLVER,
  },

  collectionDetail: {
    mediaItem: MEDIA_URL_FIELD_RESOLVER,
    processedMediaId: MEDIA_URL_FIELD_RESOLVER,
  },

  cashbackOverrideDetails: {
    targets: createCashbackOverrideTargetsResolver(),
  },

  tierDetails: {
    profileIconProcessedMediaId: MEDIA_URL_FIELD_RESOLVER,
    profileIconMediaUrl: MEDIA_URL_FIELD_RESOLVER,
    walletProcessedMediaId: MEDIA_URL_FIELD_RESOLVER,
    walletMediaUrl: MEDIA_URL_FIELD_RESOLVER,
  },

  tags: {
    imageUrl: MEDIA_URL_FIELD_RESOLVER,
    processedMediaId: MEDIA_URL_FIELD_RESOLVER,
  },

  menuCategories: {
    order: createZeroBasedOrderResolver(),
  },
} as const satisfies Record<string, Record<string, FieldResolver>>;

export type HistoryFieldResolverKey = keyof typeof HISTORY_FIELD_RESOLVERS;

export const MENU_HISTORY_FIELD_RESOLVERS: Record<string, FieldResolver> = {
  ...HISTORY_FIELD_RESOLVERS.brandMenuDish,
  ...HISTORY_FIELD_RESOLVERS.menuCategories,
};
